import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import type { AxiosError } from 'axios';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import type { UserFilterValue } from '../../users';
import { useAudiencePreview, useSendNotificationCampaign } from '../hooks/useNotificationCampaigns';
import type { AdminNotificationApiError, AudiencePreviewRequest, CampaignDto } from '../types';

interface SendConfirmDialogProps {
  open: boolean;
  campaign: CampaignDto | null;
  onClose: () => void;
  onSent: (campaign: CampaignDto) => void;
}

const audienceRequest = (campaign: CampaignDto): AudiencePreviewRequest => ({
  audienceType: campaign.audienceType,
  filter: campaign.audienceFilter || undefined,
  userPublicIds:
    campaign.audienceType === 'USERS'
      ? campaign.audienceUsers.map((user) => user.publicId)
      : undefined,
  channels: campaign.channels,
  category: campaign.category,
});

const audienceSummary = (campaign: CampaignDto) => {
  if (campaign.audienceType === 'ALL') return 'All current registered users';
  if (campaign.audienceType === 'USERS') return `${campaign.audienceUsers.length} selected users`;
  const filter = (campaign.audienceFilter || {}) as UserFilterValue;
  return (
    Object.entries(filter)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ') || 'Segment filters'
  );
};

const errorMessage = (error: unknown) => {
  const response = (error as AxiosError<AdminNotificationApiError>).response?.data;
  if (response?.error === 'EMPTY_AUDIENCE') return 'Audience is empty.';
  if (response?.error === 'ALL_CAMPAIGN_IN_PROGRESS') return 'Another ALL campaign is sending.';
  if (response?.error === 'CONFIRM_TEXT_REQUIRED') return 'Type SEND to confirm this ALL campaign.';
  if (response?.error === 'CAMPAIGN_NOT_SENDABLE') return 'This campaign can no longer be sent.';
  return response?.message || 'Unable to send campaign.';
};

export const SendConfirmDialog: React.FC<SendConfirmDialogProps> = ({
  open,
  campaign,
  onClose,
  onSent,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [changedCount, setChangedCount] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const request = useMemo(() => (campaign ? audienceRequest(campaign) : undefined), [campaign]);
  const { data: preview, isFetching } = useAudiencePreview(request, open && Boolean(campaign), 0);
  const sendMutation = useSendNotificationCampaign();

  useEffect(() => {
    if (!open) {
      setConfirmText('');
      setChangedCount(null);
    }
  }, [open]);

  if (!campaign) return null;

  const inAppCount = preview?.inAppRecipients ?? campaign.inAppCreated ?? campaign.totalRecipients;
  const emailCount =
    preview?.email?.recipients ??
    (campaign.email
      ? Object.values(campaign.email).reduce((total, count) => total + count, 0)
      : campaign.totalRecipients);
  const recipientCount =
    changedCount ?? (campaign.channels.includes('IN_APP') ? inAppCount : emailCount);
  const emailEnabled = campaign.channels.includes('EMAIL');
  const title =
    campaign.content[campaign.defaultLocale]?.title ||
    campaign.content.vi?.title ||
    campaign.content.en?.title ||
    'Untitled';
  const canSubmit =
    !sendMutation.isPending &&
    !isFetching &&
    (campaign.audienceType !== 'ALL' || confirmText === 'SEND');

  const handleSubmit = async () => {
    try {
      const sent = await sendMutation.mutateAsync({
        publicId: campaign.publicId,
        data: {
          confirmRecipientCount: recipientCount,
          confirmText: campaign.audienceType === 'ALL' ? confirmText : undefined,
        },
      });
      enqueueSnackbar('Notification campaign is sending.', { variant: 'success' });
      onSent(sent);
    } catch (error) {
      const response = (error as AxiosError<AdminNotificationApiError>).response?.data;
      if (
        response?.error === 'AUDIENCE_CHANGED' &&
        (response.data?.inAppRecipients !== undefined ||
          response.data?.emailRecipients !== undefined)
      ) {
        const nextCount = campaign.channels.includes('IN_APP')
          ? response.data.inAppRecipients
          : response.data.emailRecipients;
        const fallbackCount =
          nextCount ?? response.data.inAppRecipients ?? response.data.emailRecipients ?? 0;
        setChangedCount(fallbackCount);
        enqueueSnackbar(
          `Audience changed to ${fallbackCount} recipients. Review and confirm again.`,
          { variant: 'warning' }
        );
        return;
      }
      enqueueSnackbar(errorMessage(error), { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Review and send</DialogTitle>
      <DialogContent className="space-y-4">
        <div className="rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-900">
          <div className="font-semibold text-gray-900 dark:text-white">
            {isFetching
              ? 'Refreshing audience...'
              : `${campaign.channels.includes('IN_APP') ? `${inAppCount} in-app` : ''}${
                  campaign.channels.includes('IN_APP') && emailEnabled ? ' · ' : ''
                }${emailEnabled ? `${emailCount} email` : ''}`}
          </div>
          <div className="mt-2 text-gray-600 dark:text-slate-300">{audienceSummary(campaign)}</div>
          <div className="mt-2 text-gray-600 dark:text-slate-300">
            {campaign.category.replace('_', ' ')} · {campaign.channels.join(', ')}
          </div>
          <div className="mt-2 text-gray-900 dark:text-white">{title}</div>
        </div>
        <Alert severity="warning">
          Sending creates inbox notifications immediately. Retracting later hides them from every
          inbox.
        </Alert>
        {emailEnabled && <Alert severity="warning">Emails cannot be recalled once sent.</Alert>}
        {campaign.audienceType === 'ALL' && (
          <TextField
            fullWidth
            label='Type "SEND"'
            value={confirmText}
            onChange={(event) => setConfirmText(event.target.value)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={() => void handleSubmit()}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};
